import { css } from '@emotion/react'
import type { DailyWorksheetDay } from '@mathflat/domain/DailyLearning'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import { endOfWeek, format, isSameDay, startOfWeek } from 'date-fns'
import { observer } from 'mobx-react'
import { useEffect, useMemo, useState } from 'react'
import { generatePath, useNavigate } from 'react-router'

import { routeName } from '~/@common/constants'
import { useDailyWorksheetRemainingTime } from '~/@common/hooks/useDailyWorksheetRemainingTime'
import { useRepository } from '~/@common/hooks/useRepository'
import { toastService } from '~/@common/services'
import { commonRepo } from '~/@common/services/repo.service'
import { typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { Icon } from '~/@common/ui/Icon/Icon'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'

import DailyLearningService from '../../@service/DailyLearning.service'
import SectionHeader from '../SectionHeader'
import { openUnavailableDayModal, openWeeklyCalendarModal } from './DailyLearningDayModals'
import DailyLearningTooltip from './DailyLearningTooltip'

const getStatusText = (service: DailyLearningService) => {
  const { isMonday, mondayStreakInfo } = service

  if (isMonday) {
    return <>오늘의 학습을 완료하면 불꽃이 모여요!</>
  }

  const { count, broken } = mondayStreakInfo

  if (broken || count === 0) {
    return <>연속 달성을 놓치지 않게 노력해봐요!</>
  }

  return (
    <>
      오늘의 학습을 <strong className="consecutive-daily-learning-count">{count}일 연속</strong>{' '}
      달성했어요!
    </>
  )
}

const TODAY_POINTER_IMAGE = s3URL.mathflatAssetsBucket.graphic('graphic-pointer.svg')

const DAILY_LEARNING_LOADING_IMAGE = s3URL.student(
  'assets/mathflat/daily_learning_fire_loading.gif',
)

const DAIILY_LEARNING_STATUS_IMAGE = {
  NOT_STARTED: s3URL.student('images/daily_learning_not_started.png'),
  IN_PROGRESS: s3URL.student('images/daily_learning_in_progress.png'),
  COMPLETED: s3URL.student('assets/mathflat/complete_fire.gif'),
}

const DAILY_LEARNING_STATUS_PRESSED_IMAGE = {
  NOT_STARTED: s3URL.student('images/daily_learning_not_started_pressed.png'),
  IN_PROGRESS: s3URL.student('images/daily_learning_in_progress_pressed.png'),
  COMPLETED: s3URL.student('assets/mathflat/completed_fire_pressed.gif'),
}

const DAYS_OF_WEEK = {
  MONDAY: '월요일',
  TUESDAY: '화요일',
  WEDNESDAY: '수요일',
  THURSDAY: '목요일',
  FRIDAY: '금요일',
  SATURDAY: '토요일',
  SUNDAY: '일요일',
}

const DAILY_LEARNING_STATUS_LABEL = {
  NOT_STARTED: '학습전',
  IN_PROGRESS: '제출전',
  COMPLETED: '제출완료',
} as const

const DailyLearningSection = () => {
  const [isStartingDailyLearning, setIsStartingDailyLearning] = useState(false)
  const [pressedDay, setPressedDay] = useState<string | null>(null)
  const [selectedWeekStart, setSelectedWeekStart] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }),
  )
  const service = useRepository(DailyLearningService)
  const { isDailyWorksheetAllowed, isParent } = commonRepo.permission
  const dailyWorksheetRemainingTime = useDailyWorksheetRemainingTime(isDailyWorksheetAllowed)

  const navigate = useNavigate()

  const weekEnd = endOfWeek(selectedWeekStart, { weekStartsOn: 1 })
  const currentWeekMonday = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), [])
  const isCurrentWeek = isSameDay(selectedWeekStart, currentWeekMonday)

  useEffect(() => {
    if (!commonRepo.student?.id) return
    service.loadDailyLearning(commonRepo.student?.id, {
      startDate: format(selectedWeekStart, 'yyyy-MM-dd'),
      endDate: format(weekEnd, 'yyyy-MM-dd'),
    })
  }, [commonRepo.student?.id, selectedWeekStart])

  const handleDayClick = async (day: DailyWorksheetDay) => {
    if (isStartingDailyLearning) return

    mixpanelClient.track('오늘의학습_불꽃버튼_click', {
      학습진행여부: DAILY_LEARNING_STATUS_LABEL[day.status],
    })

    // 과거 일자 중 미완료는 진입 불가
    if (service.isPast(day) && day.status !== 'COMPLETED') {
      openUnavailableDayModal()
      return
    }

    // 학부모는 COMPLETED인 날만 진입 가능
    if (isParent && day.status !== 'COMPLETED') {
      openUnavailableDayModal()
      return
    }

    // 학습지가 있으면 상태에 따라 결과 또는 이어서 학습 페이지로 이동
    if (day.studentWorksheetId !== null) {
      mixpanelClient.track(
        day.status === 'COMPLETED'
          ? '오늘의학습_학습결과보기버튼_Click'
          : '오늘의학습_이어서학습하기버튼_Click',
        { 최대연속학습수행일자: service.maxConsecutiveWeeklyLearningCount },
      )

      // 부모는 채점 페이지, 학생은 완료 여부에 따라 분기
      if (isParent) {
        navigate(
          generatePath(routeName.parent.studentWorksheetScoringById, {
            studentWorksheetId: day.studentWorksheetId,
          }),
        )
        return
      }

      navigate(
        generatePath(
          day.status === 'COMPLETED'
            ? routeName.student.studentWorksheetScoringById
            : routeName.student.studentWorksheetById,
          { studentWorksheetId: day.studentWorksheetId },
        ),
      )
      return
    }

    // 학습지 없음 + (오늘이 아니거나 부모 계정)이면 진입 불가
    if (!service.isToday(day) || isParent) {
      openUnavailableDayModal()
      return
    }

    // 학습지 없음 + 오늘 + 학생: 오늘의 학습 시작
    if (!commonRepo.student?.id) return
    mixpanelClient.track('오늘의학습_오늘의학습시작하기버튼_Click', {
      최대연속학습수행일자: service.maxConsecutiveWeeklyLearningCount,
    })
    try {
      setIsStartingDailyLearning(true)
      const studentWorksheetId = await service.startDailyLearning()
      navigate(generatePath(routeName.student.studentWorksheetById, { studentWorksheetId }))
    } catch (error) {
      toastService.error('학습범위가 설정되지 않아 학습할 수 없어요. 선생님에게 문의하세요.')
    } finally {
      setIsStartingDailyLearning(false)
    }
  }

  const weekRangeText = isCurrentWeek
    ? `이번주 ${format(selectedWeekStart, 'MM/dd')} - ${format(weekEnd, 'MM/dd')}`
    : `${format(selectedWeekStart, 'MM/dd')} - ${format(weekEnd, 'MM/dd')}`

  let pastWeekStreakCount = 0
  if (!isCurrentWeek) {
    for (const day of service.dailyLearning) {
      if (day.status === 'COMPLETED') pastWeekStreakCount++
      else break
    }
  }

  if (!service.dailyLearning.length) return null

  return (
    <>
      <section css={S}>
        <SectionHeader title="오늘의 학습">
          <DailyLearningTooltip />
        </SectionHeader>
        <div className="content">
          <>
            <div className="content-header">
              <div className="date-chip-group">
                <button
                  className={`date-chip${isCurrentWeek ? '' : ' past'}`}
                  type="button"
                  onClick={() => {
                    mixpanelClient.track('오늘의학습_캘린더버튼_click')
                    openWeeklyCalendarModal(selectedWeekStart, setSelectedWeekStart)
                  }}
                >
                  <Icon
                    name="icon_calendar"
                    size={16}
                    color={isCurrentWeek ? 'var(--Colors-Gray-900)' : 'var(--Colors-Indigo-400)'}
                  />
                  {weekRangeText}
                </button>
                {!isCurrentWeek && (
                  <button
                    className="go-to-current-week"
                    type="button"
                    onClick={() => setSelectedWeekStart(currentWeekMonday)}
                  >
                    이번주로가기
                    <Icon name="icon_reset" size={16} color="var(--Colors-Gray-900)" />
                  </button>
                )}
              </div>
              {!isParent &&
                isCurrentWeek &&
                (service.todayLearning?.status === 'COMPLETED' ? (
                  <span className="remaining-time completed">학습완료</span>
                ) : (
                  <div className="remaining-time">
                    <img
                      src={s3URL.mathflatAssetsBucket.graphic('graphic-timer.svg')}
                      alt="타이머 아이콘"
                    />
                    {format(dailyWorksheetRemainingTime, 'HH:mm:ss')}
                  </div>
                ))}
            </div>

            <div className="learning-status">
              {isParent ? (
                <>요일별로 학생이 학습한 결과를 확인해보세요!</>
              ) : isCurrentWeek ? (
                getStatusText(service)
              ) : pastWeekStreakCount > 0 ? (
                <>
                  오늘의 학습을{' '}
                  <strong className="consecutive-daily-learning-count">
                    {pastWeekStreakCount}일 연속
                  </strong>{' '}
                  달성했어요!
                </>
              ) : (
                <>연속 달성을 놓치지 않게 노력해봐요!</>
              )}
            </div>

            <div className="learning-weekly-info">
              {service.dailyLearning?.map((day) => {
                const isToday = service.isToday(day)
                return (
                  <button
                    key={day.day}
                    type="button"
                    className={`weekly-day-container${isToday ? ' today' : ''}`}
                    onClick={() => handleDayClick(day)}
                    onPointerDown={() => setPressedDay(day.day)}
                    onPointerUp={() => setPressedDay(null)}
                    onPointerLeave={() => setPressedDay(null)}
                    role="button"
                  >
                    {isToday && <img src={TODAY_POINTER_IMAGE} alt="" className="today-pointer" />}
                    <img
                      src={
                        isToday && isStartingDailyLearning
                          ? DAILY_LEARNING_LOADING_IMAGE
                          : pressedDay === day.day
                            ? DAILY_LEARNING_STATUS_PRESSED_IMAGE[day.status]
                            : DAIILY_LEARNING_STATUS_IMAGE[day.status]
                      }
                      alt={day.day}
                    />
                    <span className="weekly-day">{isToday ? '오늘' : DAYS_OF_WEEK[day.day]}</span>
                  </button>
                )
              })}
            </div>
          </>
        </div>
      </section>
    </>
  )
}

export default observer(DailyLearningSection)

const S = css`
  display: flex;
  flex-direction: column;

  .content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 24px;
    background-color: var(--White);
    border-radius: var(--Radius-300);

    .content-header {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .date-chip-group {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .date-chip {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 6px 12px;
      border: 1px solid var(--Colors-Gray-400);
      border-radius: var(--Radius-Circle);
      background: var(--White);
      ${typo.body02};
      color: var(--Colors-Gray-900);

      &.past {
        background: var(--Colors-Indigo-50);
        border-color: var(--Colors-Indigo-200);
        color: var(--Colors-Indigo-400);
      }
      cursor: pointer;
    }

    .go-to-current-week {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 6px 12px;
      border: 1px solid var(--Colors-Gray-400);
      border-radius: var(--Radius-Circle);
      background: var(--White);
      ${typo.body02};
      color: var(--Colors-Gray-900);
      cursor: pointer;
    }

    .remaining-time {
      display: flex;
      align-items: center;
      gap: 2px;
      ${typo.caption01};
      color: var(--Colors-Blue-600);
      font-weight: 700;

      &.completed {
        color: var(--Colors-Blue-600);
      }

      > img {
        width: 16px;
        height: 16px;
      }
    }

    .learning-status {
      ${typo.heading05};
      color: var(--Colors-Gray-900);
      font-weight: 700;
    }

    .learning-weekly-info {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 18px 16px;
      padding: 20px;
      border: 2px solid var(--Colors-Gray-200);
      border-radius: var(--Radius-400);

      ${mediaQuery.underDesktop} {
        padding: 20px 10px;
        gap: 18px 10px;
      }

      ${mediaQuery.underTablet} {
        padding: 20px;
        gap: 18px 16px;
      }

      .weekly-day-container {
        border: none;
        background: transparent;
        padding: 0;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        justify-content: flex-end;
        cursor: pointer;

        .today-pointer {
          position: absolute;
          top: -12px;
          left: 50%;
          width: 12px;
          transform: translateX(-50%);
          animation: bounceY 1s ease-in-out infinite;
        }

        @keyframes bounceY {
          0%,
          100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-4px);
          }
        }

        img {
          width: 48px;
          border-radius: var(--Radius-400);
          transition: transform 0.2s ease;
        }

        .weekly-day {
          ${typo.caption01};
          color: var(--Colors-Gray-600);
        }

        &.today .weekly-day {
          color: var(--Colors-Blue-600);
          font-weight: 700;
        }
      }
    }

    .consecutive-daily-learning-count {
      color: var(--Colors-Blue-600);
      font-weight: 700;
    }
  }
`
